import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Zap, MessageSquareText, Clock, Repeat, MessagesSquare, Building2 } from "lucide-react";

/* THE DATA, industry reality + the 977-request study + portfolio multiplier.
   Same figures as the live homepage; presented in the concept's visual language. */

const INDUSTRY = [
  [66, "%", "of boards that switch managers blame poor responsiveness & communication"],
  [71, "%", "of community managers report moderate-to-severe job burnout"],
  [97, "%", "of management-company leaders say the manager shortage is hurting growth"],
  [20, "%", "of the community-management workforce turns over in a typical year"],
];

const HIDDEN_COST = [
  { stat: "2.94", icon: MessageSquareText, tag: "Response load", label: "written staff responses per request", sub: "Nearly three replies for every request, most of it administrative back-and-forth." },
  { stat: "42%", icon: Clock, tag: "Slow today", label: "get a same-day answer today", sub: "Residents wait a day or more. The concierge answers instantly, 24/7." },
  { stat: "39%", icon: Repeat, tag: "Chasing replies", label: "had to message again after a reply", sub: "Nearly 4 in 10 residents followed up because the first answer was not enough." },
  { stat: "50%", icon: MessagesSquare, tag: "Long threads", label: "became 4+ message conversations", sub: "Half of all requests spiral into long threads. AI cuts the back-and-forth." },
];

const THEMES = [
  ["HVAC, electrical & building maintenance", 36], ["Access, keys, fobs & callbox", 25],
  ["Amenities, pets, parking & rules", 18], ["Documents, forms & account support", 16],
  ["Leasing, tenants & move-ins", 15], ["Payments, dues, statements & fines", 11],
  ["Insurance, leaks, water & mold", 7], ["Security, incidents & footage", 6],
];

function CountUp({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 1.6, ease: [0.16, 1, 0.3, 1], onUpdate: setVal });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{Math.round(val).toLocaleString()}{suffix}</span>;
}

export function IndustryBand() {
  return (
    <section className="rm-evidence" data-testid="concept-industry-band" aria-labelledby="concept-industry-heading">
      <div className="rm-container">
        <div className="rm-evidence-head">
          <div>
            <p className="rm-kicker">THE INDUSTRY REALITY</p>
            <h2 id="concept-industry-heading">Service <span>is</span> the business, and it's the part that's breaking.</h2>
          </div>
          <p>Boards rarely leave over price. They leave when residents don't hear back. And the managers expected to answer are stretched past the limit, exactly the gap a concierge closes.</p>
        </div>
        <div className="rm-evidence-grid">
          {INDUSTRY.map(([v, s, label], i) => (
            <div key={i} className="rm-evidence-stat" data-testid={`concept-industry-stat-${i}`}>
              <strong><CountUp to={v} suffix={s} /></strong>
              <p>{label}</p>
            </div>
          ))}
        </div>
        <div className="rm-evidence-foot">
          <p>The fix isn't hiring more managers you can't find, it's giving the ones you have a 24/7 concierge and a copilot.</p>
          <span>Sources: industry surveys of HOA boards, community managers &amp; management-company executives.</span>
        </div>
      </div>
    </section>
  );
}

function PortfolioMultiplier() {
  const [count, setCount] = useState(50);
  const fmt = (n) => n.toLocaleString();
  const rows = [
    [MessageSquareText, fmt(230 * count), "", "resident requests answered or prepared by AI, every year"],
    [Repeat, fmt(675 * count), "", "staff replies lifted off your managers' plates, every year"],
    [Clock, fmt(28 * count), " hrs", `staff time returned, every year, about ${Math.round((28 * count) / 8).toLocaleString()} full workdays`],
  ];
  return (
    <div className="rm-multiplier" data-testid="concept-portfolio-calculator">
      <div className="rm-multiplier-controls">
        <p className="rm-kicker"><Building2 size={12} /> PORTFOLIO IMPACT</p>
        <h3>Your whole portfolio, on the same footing.</h3>
        <p>Set your portfolio size and see the workload the concierge lifts off your team, year after year.</p>
        <div className="rm-multiplier-count"><span>Communities you manage</span><strong data-testid="concept-portfolio-count">{count.toLocaleString()}</strong></div>
        <input type="range" min="1" max="5000" value={count} onChange={(e) => setCount(Number(e.target.value))} aria-label="Communities you manage" data-testid="concept-portfolio-slider" />
        <div className="rm-multiplier-scale"><span>1</span><span>5,000+ communities</span></div>
        <div className="rm-multiplier-presets">
          {[10, 50, 250, 1000, 5000].map((n) => (
            <button key={n} type="button" onClick={() => setCount(n)} data-testid={`concept-portfolio-preset-${n}`} className={count === n ? "is-active" : ""}>{n.toLocaleString()}</button>
          ))}
        </div>
      </div>
      <div className="rm-multiplier-results">
        {rows.map(([Icon, value, suffix, label]) => (
          <div key={label} className="rm-multiplier-result">
            <Icon size={18} />
            <strong>{value}<em>{suffix}</em></strong>
            <p>{label}</p>
          </div>
        ))}
      </div>
      <p className="rm-fine-print rm-multiplier-note">Projected from the 977-request study (~734 staff responses and 92% AI-addressable per community per year) using conservative savings assumptions. Written messages only, excludes phone calls, document searches, work-order creation, vendor follow-up, after-hours coverage and resident wait time.</p>
    </div>
  );
}

export function StudySection() {
  return (
    <section id="concept-data" className="rm-study rm-section" data-testid="concept-study-section" aria-labelledby="concept-study-heading">
      <div className="rm-container">
        <div className="rm-section-heading">
          <div>
            <p className="rm-kicker"><span>DATA</span> ANALYZED FROM 977 REAL RESIDENT REQUESTS</p>
            <h2 id="concept-study-heading">Your managers answer the same requests over and over. <span>AI takes 92% off their plate.</span></h2>
          </div>
          <p>We classified every one of 977 resident request threads from a live community, 904 pages, 5,079 messages. Here is what a team really spends its day on, and how much of it a concierge handles.</p>
        </div>

        <div className="rm-study-grid">
          <motion.div className="rm-study-hero" data-testid="concept-study-92" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}>
            <p className="rm-small-label"><Zap size={11} /> AUTOMATION OPPORTUNITY</p>
            <strong>92<em>%</em></strong>
            <p className="rm-study-hero-copy">of resident requests AI can <b>fully resolve or substantially prepare</b> before a manager is ever involved.</p>
            <div className="rm-study-bar" aria-hidden="true">
              <motion.span style={{ background: "#ED7373" }} initial={{ width: 0 }} whileInView={{ width: "45%" }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }} />
              <motion.span style={{ background: "#ED737380" }} initial={{ width: 0 }} whileInView={{ width: "47%" }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.45 }} />
            </div>
            <ul className="rm-study-legend">
              {[["45%", "Fully AI-resolvable", "Access, policies, leasing, documents, payments, account questions", "#ED7373"], ["47%", "AI-assisted", "Maintenance & incidents, AI collects details, photos, opens & routes the case", "#ED737380"], ["8%", "Human-led", "Waivers, disputes, exceptions, legal review, board decisions", "#ffffff40"]].map(([pct, name, desc, dot]) => (
                <li key={name}><i style={{ background: dot }} /><span><b>{pct}</b> {name} <small>, {desc}</small></span></li>
              ))}
            </ul>
          </motion.div>

          <div className="rm-study-costs">
            {HIDDEN_COST.map((s, i) => (
              <motion.div key={s.stat} className="rm-study-cost" data-testid={`concept-study-stat-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, delay: 0.08 * i }}>
                <div className="rm-study-cost-top"><s.icon size={17} /><span className="rm-small-label">{s.tag.toUpperCase()}</span></div>
                <strong>{s.stat}</strong>
                <p>{s.label}</p>
                <small>{s.sub}</small>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="rm-themes" data-testid="concept-study-themes">
          <div className="rm-themes-head">
            <p className="rm-kicker">WHERE THE TIME ACTUALLY GOES</p>
            <h3>It isn't just FAQs, it's the whole operation.</h3>
            <p>The 977 requests, by theme. Every one of these is something the concierge can answer, intake or route.</p>
          </div>
          <ul className="rm-themes-list">
            {THEMES.map(([name, pct], i) => (
              <li key={name}>
                <span>{name}</span>
                <div className="rm-themes-bar"><motion.i initial={{ width: 0 }} whileInView={{ width: `${(pct / 36) * 100}%` }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.9, delay: i * 0.05 }} /></div>
                <b>{pct}%</b>
              </li>
            ))}
          </ul>
          <p className="rm-fine-print">Themes overlap because a single request can involve more than one issue.</p>
        </div>

        <PortfolioMultiplier />

        <p className="rm-fine-print rm-study-source">Based on an independent analysis of <b>904 pages and 977 top-level resident request threads (5,079 messages)</b> from a live community's ticket history.</p>
      </div>
    </section>
  );
}
